import React, { useState, useEffect } from 'react';
import './ImpactDashboard.css';

const ImpactDashboard = ({ onStartNew }) => {
  const [carbonGenerated, setCarbonGenerated] = useState(2.3);
  const [marketValue, setMarketValue] = useState(116);
  const [hectaresRestored, setHectaresRestored] = useState(150);
  const [co2Sequestered, setCo2Sequestered] = useState(2407);
  const [communityIncome, setCommunityIncome] = useState(15997);
  const [biodiversityIndex, setBiodiversityIndex] = useState(8037);
  const [monitoringPeriod, setMonitoringPeriod] = useState('7d');

  // Animate values
  useEffect(() => {
    const interval = setInterval(() => {
      setCarbonGenerated(prev => +(prev + (Math.random() * 0.02 - 0.01)).toFixed(2));
      setMarketValue(prev => +(prev + (Math.random() * 2 - 1)).toFixed(0));
      setHectaresRestored(prev => +(prev + (Math.random() * 0.5)).toFixed(0));
      setCo2Sequestered(prev => +(prev + (Math.random() * 10 - 5)).toFixed(0));
      setCommunityIncome(prev => +(prev + (Math.random() * 50 - 25)).toFixed(0));
      setBiodiversityIndex(prev => +(prev + (Math.random() * 20 - 10)).toFixed(0));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const downloadPDF = () => {
    // Create a simple PDF report
    const reportContent = `
      BLUE CARBON IMPACT DASHBOARD REPORT
      ===================================
      
      Project Overview:
      - Carbon Generated: ${carbonGenerated} tons
      - Market Value: $${marketValue}
      - Project Status: Active
      - Monitoring Since: 10/10/2023
      
      Monitoring Period: ${monitoringPeriod}
      
      Key Metrics:
      - Hectares Restored: ${hectaresRestored}ha
      - CO₂ Sequestered: ${co2Sequestered}kg
      - Community Income: $${communityIncome}
      - Biodiversity Index: ${biodiversityIndex}/100
      
      Progress Indicators:
      - Restoration Progress: 78%
      - Carbon Target: 65%
      - Community Impact: 82%
      - Biodiversity: 71%
      
      Environmental Health:
      - Water Quality: Improved
      - Vegetation Health: Excellent
      - Marine Life: Recovering
      
      Community Benefits:
      - Families Supported: 156
      - Jobs Created: 12
      - Training programs for sustainable practices
      - Alternative livelihood opportunities
      - Women-led initiatives
      
      Monitoring Details:
      - Satellite Imagery: Active
      - IoT Sensors: 12 devices
      - Field Reports: Monthly
      - Community Monitoring: Active
      
      Generated: ${new Date().toLocaleString()}
    `;
    
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Blue-Carbon-Report-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    alert('📄 Detailed analysis report downloaded!');
  };

  const handleStartNew = () => {
    if (onStartNew) {
      onStartNew();
    }
  };

  return (
    <div className="impact-dashboard">
      <header className="impact-header">
        <div className="header-content">
          <h1>Impact Dashboard</h1>
          <p>Real-time Environmental Monitoring</p>
        </div>
        <button className="new-delhi-btn">📍 New Delhi, India</button>
      </header>

      <div className="impact-container">
        {/* Project Overview */}
        <section className="impact-section">
          <div className="impact-section-header">📊 Project Overview</div>
          <div className="overview-grid">
            <div className="overview-card">
              <div className="overview-value">{carbonGenerated}</div>
              <div className="overview-label">Carbon Generated</div>
            </div>
            <div className="overview-card">
              <div className="overview-value">${marketValue}</div>
              <div className="overview-label">Market Value</div>
            </div>
          </div>
          <div className="overview-details">
            <div className="detail-row">
              <span>Project Status:</span>
              <strong className="status-active">Active</strong>
            </div>
            <div className="detail-row">
              <span>Monitoring Since:</span>
              <strong>10/10/2023</strong>
            </div>
          </div>
        </section>

        {/* Monitoring Period */}
        <section className="impact-section">
          <div className="impact-section-header">📅 Monitoring Period</div>
          <div className="period-buttons">
            <button className={monitoringPeriod === '7d' ? 'period-btn active' : 'period-btn'} onClick={() => setMonitoringPeriod('7d')}>7d</button>
            <button className={monitoringPeriod === '30d' ? 'period-btn active' : 'period-btn'} onClick={() => setMonitoringPeriod('30d')}>30d</button>
            <button className={monitoringPeriod === '90d' ? 'period-btn active' : 'period-btn'} onClick={() => setMonitoringPeriod('90d')}>90d</button>
          </div>
        </section>

        {/* Key Metrics */}
        <section className="impact-section">
          <div className="metrics-row">
            <div className="metric-box">
              <div className="metric-icon">🌊</div>
              <div className="metric-data">
                <div className="metric-value">{hectaresRestored}ha</div>
                <div className="metric-label">Hectares Restored</div>
                <div className="metric-change">+12%</div>
              </div>
            </div>
            <div className="metric-box">
              <div className="metric-icon">🌍</div>
              <div className="metric-data">
                <div className="metric-value">{co2Sequestered}kg</div>
                <div className="metric-label">CO₂ Sequestered</div>
                <div className="metric-change">+8%</div>
              </div>
            </div>
          </div>
        </section>

        <section className="impact-section">
          <div className="metrics-row">
            <div className="metric-box">
              <div className="metric-icon">💰</div>
              <div className="metric-data">
                <div className="metric-value">${communityIncome}</div>
                <div className="metric-label">Community Income</div>
                <div className="metric-change">+15%</div>
              </div>
            </div>
            <div className="metric-box">
              <div className="metric-icon">🦋</div>
              <div className="metric-data">
                <div className="metric-value">{biodiversityIndex}/100</div>
                <div className="metric-label">Biodiversity Index</div>
                <div className="metric-change">+20%</div>
              </div>
            </div>
          </div>
        </section>

        {/* Progress Indicators */}
        <section className="impact-section">
          <div className="impact-section-header">📈 Progress Indicators</div>
          <div className="progress-bars">
            <div className="progress-bar-item">
              <div className="progress-bar-header">
                <span>Restoration Progress</span>
                <span>78%</span>
              </div>
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: '78%' }}></div>
              </div>
            </div>
            <div className="progress-bar-item">
              <div className="progress-bar-header">
                <span>Carbon Target</span>
                <span>65%</span>
              </div>
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: '65%' }}></div>
              </div>
            </div>
            <div className="progress-bar-item">
              <div className="progress-bar-header">
                <span>Community Impact</span>
                <span>82%</span>
              </div>
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: '82%' }}></div>
              </div>
            </div>
            <div className="progress-bar-item">
              <div className="progress-bar-header">
                <span>Biodiversity</span>
                <span>71%</span>
              </div>
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: '71%' }}></div>
              </div>
            </div>
          </div>
        </section>

        {/* Environmental Health */}
        <section className="impact-section">
          <div className="impact-section-header">🌿 Environmental Health</div>
          <div className="health-list">
            <div className="health-row">
              <span>💧 Water Quality</span>
              <span className="health-status improved">Improved</span>
            </div>
            <div className="health-row">
              <span>🌱 Vegetation Health</span>
              <span className="health-status excellent">Excellent</span>
            </div>
            <div className="health-row">
              <span>🐟 Marine Life</span>
              <span className="health-status recovering">Recovering</span>
            </div>
          </div>
        </section>

        {/* Community Benefits */}
        <section className="impact-section">
          <div className="impact-section-header">👥 Community Benefits</div>
          <div className="community-stats">
            <div className="community-stat">
              <div className="stat-number">156</div>
              <div className="stat-label">Families Supported</div>
            </div>
            <div className="community-stat">
              <div className="stat-number">12</div>
              <div className="stat-label">Jobs Created</div>
            </div>
          </div>
          <div className="community-programs">
            <div className="program-item">✓ Training programs for sustainable practices</div>
            <div className="program-item">✓ Alternative livelihood opportunities</div>
            <div className="program-item">✓ Women-led initiatives</div>
          </div>
        </section>

        {/* Monitoring Details */}
        <section className="impact-section">
          <div className="impact-section-header">🛰️ Monitoring Details</div>
          <div className="monitoring-list">
            <div className="monitoring-item">
              <span>Satellite Imagery:</span>
              <strong>Active</strong>
            </div>
            <div className="monitoring-item">
              <span>IoT Sensors:</span>
              <strong>12 devices</strong>
            </div>
            <div className="monitoring-item">
              <span>Field Reports:</span>
              <strong>Monthly</strong>
            </div>
            <div className="monitoring-item">
              <span>Community Monitoring:</span>
              <strong>Active</strong>
            </div>
          </div>
        </section>

        {/* Success Message */}
        <div className="success-message">
          <div className="success-icon">🌍</div>
          <div className="success-text">
            <strong>Project Successfully Deployed!</strong>
            <p>Your blue carbon project is now being monitored and is making a tangible impact on climate change.</p>
            <p className="success-note">Continue monitoring via satellite imagery and IoT sensors for ongoing impact tracking.</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="impact-actions">
          <button className="impact-btn download-btn" onClick={downloadPDF}>
            📄 View Detailed Analysis
          </button>
          <button className="impact-btn start-new-btn" onClick={handleStartNew}>
            🚀 Start New Monitoring Project
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpactDashboard;
